import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Scissors, ArrowRight, Sparkles, AlertCircle, Copy, ChevronDown } from "lucide-react";

export default function LoginPage({ platformMode = false }) {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  // Rich error object for detailed display + copy-to-clipboard diagnostics.
  // Shape: { headline, hint, http_status, server_detail, error_code, network_message, attempted_url, timestamp }
  const [errorDetails, setErrorDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Surface OAuth errors forwarded from AuthCallback so users aren't bounced silently.
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      // Clear state so a refresh doesn't re-show the banner.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorDetails(null);
    setShowDiagnostics(false);
    setLoading(true);
    try {
      // Auto-detect whether the input is an email or phone number; the
      // backend accepts either via {email}, {phone}, or {identifier}.
      const idValue = (email || "").trim();
      const looksLikeEmail = idValue.includes("@");
      const idPayload = looksLikeEmail ? { email: idValue } : { phone: idValue };
      if (isRegister) {
        const res = await api.post("/auth/register", { ...idPayload, password, name });
        login(res.data.user, res.data.token);
        navigate("/dashboard", { replace: true });
      } else {
        const res = await api.post("/auth/login", { ...idPayload, password });
        login(res.data.user, res.data.token);
        const role = res.data.user?.role;
        if (role === "platform_admin") {
          navigate("/platform", { replace: true });
          return;
        }
        if (platformMode) {
          setError("This account is not a platform admin. Use /login instead.");
          localStorage.removeItem("salonflow_token");
          setLoading(false);
          return;
        }
        const staffRoles = ["stylist", "esthetician", "nail_tech", "colorist", "support_staff", "front_desk"];
        navigate(staffRoles.includes(role) ? "/my-portal" : "/dashboard", { replace: true });
      }
    } catch (err) {
      // Build a detailed, copy-able error report so end-users can self-diagnose
      // and forward us a precise breadcrumb trail when something goes wrong.
      // eslint-disable-next-line no-console
      console.error("Login failed", { status: err.response?.status, data: err.response?.data, message: err.message, code: err.code });
      const status = err.response?.status;
      const serverDetail = typeof err.response?.data?.detail === "string" ? err.response.data.detail : null;
      const attemptedUrl = err.config?.baseURL ? `${err.config.baseURL}${err.config.url || ""}` : (err.config?.url || "/api/auth/login");
      const ts = new Date().toISOString();

      let headline;
      let hint;
      if (err.response) {
        if (serverDetail && serverDetail.trim()) {
          headline = serverDetail;
          if (status === 401) {
            hint = "Double-check the email and password. If you copy/pasted, make sure no trailing space slipped in. Browser autofill can also insert an old password — clear the field and type it fresh.";
          } else if (status === 403) {
            hint = "Your account exists but isn't allowed to sign in here. If you're staff, use the Employee Portal instead. If this is a platform-admin login, use /platform/login.";
          } else if (status === 429) {
            hint = "Too many attempts. Wait 60 seconds before trying again.";
          } else if (status >= 500) {
            hint = "Our server hit an error. Try again in a moment — if it persists, contact support with the diagnostics below.";
          }
        } else if (status === 401) {
          headline = "Email or password is incorrect.";
          hint = "Double-check both fields. Browser autofill sometimes inserts an old password — clear the field and type it fresh.";
        } else if (status === 403) {
          headline = "Sign-in blocked.";
          hint = "Your account doesn't have access to this page. Use the Employee Portal if you're staff.";
        } else if (status === 429) {
          headline = "Too many attempts.";
          hint = "Wait 60 seconds and try again.";
        } else if (status >= 500) {
          headline = `Server error (HTTP ${status}).`;
          hint = "Our server is having trouble. Please retry in a moment.";
        } else {
          headline = `Login blocked (HTTP ${status}).`;
          hint = "Try a different network, browser, or device. Disable VPNs/ad-blockers/security extensions and try again.";
        }
      } else if (err.code === "ECONNABORTED") {
        headline = "The server took too long to respond.";
        hint = "Your connection is slow or our server is overloaded. Wait 30 seconds and try again.";
      } else {
        headline = "Couldn't reach the server.";
        hint = "This is usually a network problem. Try: (1) a different Wi-Fi or mobile data, (2) a different browser, (3) disable VPN/ad-blocker/security extensions, (4) try Incognito/Private mode. If nothing works, your network may be blocking salon software — contact your IT.";
      }

      setError(headline);
      setErrorDetails({
        headline,
        hint,
        http_status: status || null,
        error_code: err.code || null,
        server_detail: serverDetail,
        network_message: err.message || null,
        attempted_url: attemptedUrl,
        attempted_email: email,
        is_register: isRegister,
        platform_mode: platformMode,
        timestamp: ts,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyDiagnostics = async () => {
    if (!errorDetails) return;
    const lines = [
      `Login error report — ${errorDetails.timestamp}`,
      `URL:     ${errorDetails.attempted_url}`,
      `Email:   ${errorDetails.attempted_email || "—"}`,
      `Mode:    ${errorDetails.is_register ? "register" : (errorDetails.platform_mode ? "platform login" : "login")}`,
      `HTTP:    ${errorDetails.http_status ?? "(no response)"}`,
      `Code:    ${errorDetails.error_code ?? "—"}`,
      `Server:  ${errorDetails.server_detail || "—"}`,
      `Network: ${errorDetails.network_message || "—"}`,
      `Headline: ${errorDetails.headline}`,
      `Hint:    ${errorDetails.hint || "—"}`,
      `Browser: ${errorDetails.user_agent}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(lines);
    } catch { /* noop — clipboard may be blocked */ }
  };

  const handleGoogleLogin = () => {
    // Pass only the origin (no path) — auth.emergentagent.com appends the
    // session_id as a hash to this URL on the way back. AuthCallback picks
    // it up and routes to /dashboard.
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-fadeIn">
          {/* Brand */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center shadow-lg shadow-rose-500/20">
              <Scissors className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900">
              SalonFlow
            </span>
          </div>

          <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight text-slate-900 mb-2">
            {platformMode ? "Platform Admin" : isRegister ? "Create account" : "Welcome back"}
          </h1>
          <p className="text-slate-500 mb-10 text-base font-['Manrope']">
            {platformMode ? "Super-admin access to all workspaces" : isRegister ? "Start managing your salon today" : "Sign in to your salon dashboard"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {isRegister && (
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block font-['Manrope']">Full Name</label>
                <Input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="h-12 bg-slate-50 border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900"
                  required
                />
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block font-['Manrope']">Email or phone number</label>
              <Input
                data-testid="login-email-input"
                type="text"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@salon.com or (555) 555-5555"
                className="h-12 bg-slate-50 border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900 text-base"
                required
              />
              <p className="text-[11px] text-slate-400 mt-1.5 font-['Manrope']">
                Use whichever you registered with — email, phone, or both.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-600 block font-['Manrope']">Password</label>
                {!isRegister && (
                  <Link
                    to="/forgot-password"
                    className="text-[11px] font-semibold uppercase tracking-wider text-rose-600 hover:text-rose-700 font-['Manrope']"
                    data-testid="forgot-password-link"
                  >
                    Forgot?
                  </Link>
                )}
              </div>
              <Input
                data-testid="login-password-input"
                type="password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete={isRegister ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 bg-slate-50 border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900 text-base"
                required
              />
            </div>

            {error && (
              <div data-testid="auth-error-message" className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 font-['Manrope']">
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-rose-700" data-testid="auth-error-headline">{error}</p>
                    {errorDetails?.hint && (
                      <p className="text-[12px] text-rose-600/80 mt-1 leading-relaxed" data-testid="auth-error-hint">
                        {errorDetails.hint}
                      </p>
                    )}
                    {errorDetails && (
                      <>
                        <button type="button"
                          onClick={() => setShowDiagnostics(s => !s)}
                          data-testid="toggle-diagnostics"
                          className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700/90 hover:text-rose-800 underline-offset-2 hover:underline">
                          <ChevronDown className={`w-3 h-3 transition-transform ${showDiagnostics ? "rotate-180" : ""}`} />
                          {showDiagnostics ? "Hide" : "Show"} technical details
                        </button>
                        {showDiagnostics && (
                          <div className="mt-2 rounded-lg bg-white/70 border border-rose-200 px-3 py-2.5 text-[11px] text-slate-700 font-mono leading-relaxed space-y-0.5" data-testid="auth-diagnostics">
                            <div><span className="text-slate-400">Time:</span> {errorDetails.timestamp}</div>
                            <div><span className="text-slate-400">Status:</span> {errorDetails.http_status ?? "(no HTTP response)"}</div>
                            {errorDetails.error_code && (
                              <div><span className="text-slate-400">Code:</span> {errorDetails.error_code}</div>
                            )}
                            {errorDetails.server_detail && (
                              <div><span className="text-slate-400">Server:</span> {errorDetails.server_detail}</div>
                            )}
                            {errorDetails.network_message && (
                              <div><span className="text-slate-400">Network:</span> {errorDetails.network_message}</div>
                            )}
                            <div className="break-all"><span className="text-slate-400">URL:</span> {errorDetails.attempted_url}</div>
                            <div className="break-all"><span className="text-slate-400">Email:</span> {errorDetails.attempted_email || "—"}</div>
                            <button type="button"
                              onClick={() => { copyDiagnostics().then(() => { /* swallow */ }); }}
                              data-testid="copy-diagnostics-btn"
                              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 hover:bg-rose-200 text-[10px] font-bold uppercase tracking-wider text-rose-700 transition-colors">
                              <Copy className="w-3 h-3" /> Copy details for support
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 gradient-primary text-white rounded-xl text-base font-semibold shadow-[0_4px_14px_rgba(244,63,94,0.35)] hover:shadow-[0_6px_20px_rgba(244,63,94,0.25)] hover:-translate-y-0.5 transition-all duration-200"
            >
              {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-sm text-slate-400 font-['Manrope']">or</span></div>
          </div>

          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-12 border-slate-200 hover:bg-slate-50 rounded-xl text-base font-medium transition-all duration-200 text-slate-700 shadow-sm"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <p className="text-center mt-8 text-sm text-slate-500 font-['Manrope']">
            {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="text-rose-600 font-semibold hover:text-rose-700 transition-colors"
            >
              {isRegister ? "Sign in" : "Create one"}
            </button>
          </p>

          {!isRegister && !platformMode && (
            <p className="text-center mt-3 text-sm text-slate-400 font-['Manrope']">
              New to SalonFlow?{" "}
              <a href="/signup" data-testid="login-to-signup" className="text-slate-700 font-semibold hover:text-slate-900 transition-colors underline underline-offset-2">
                Create your own workspace
              </a>
            </p>
          )}

          {!isRegister && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <button
                data-testid="staff-portal-btn"
                onClick={() => window.location.href = "/employee/login"}
                className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600 transition-all duration-200"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
                Employee Portal
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right - Hero Visual */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden gradient-secondary">
        {/* Decorative shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-orange-400/20 blur-2xl" />
        </div>
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end p-14">
          <div className="backdrop-blur-xl bg-white/10 rounded-3xl p-8 border border-white/20">
            <Sparkles className="w-8 h-8 text-white/80 mb-4" strokeWidth={1.5} />
            <p className="font-['Outfit'] text-3xl text-white font-semibold leading-tight tracking-tight">
              The operating system<br />for modern beauty
            </p>
            <p className="text-white/70 mt-4 text-base font-['Manrope'] leading-relaxed">
              Scheduling, CRM, marketing, and business management — all in one beautiful place.
            </p>
          </div>
          <div className="flex items-center gap-4 mt-8 ml-2">
            <div className="flex -space-x-3">
              {["S", "E", "J", "M"].map((l, i) => (
                <div key={i} className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-white text-sm font-semibold">
                  {l}
                </div>
              ))}
            </div>
            <p className="text-white/70 text-sm font-['Manrope']">Trusted by 500+ salons worldwide</p>
          </div>
        </div>
      </div>
    </div>
  );
}
